import { Button, MultiSelect, Stack } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../components/SimpleBreadcrumbs";
import { FormattedMessage, useIntl } from "react-intl";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { useTRPC } from "../../trpc";
import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "../../components/Loader";
import { AgGridTable } from "../../components/AgGridTable.js";

export const Maps = () => {
  const intl = useIntl();
  const trpc = useTRPC();

  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedSourceStyles, setSelectedSourceStyles] = useState<string[]>([]);

  const { data: accounts = [] } = useQuery(trpc.getAccounts.queryOptions());
  const { data: publicStyles = [] } = useQuery(trpc.getPublicStyles.queryOptions());
  const { data: styles = [], isLoading } = useQuery(trpc.getAccountsStyles.queryOptions({
    accountIds: selectedAccounts.map(Number),
    sourceStylesIds: selectedSourceStyles.map(Number),
  }));

  const stylesWithAccounts = useMemo(() => styles.map(c => ({
    ...c,
    accountName: accounts.find(a => a.id === c.accountId)?.name,
  })), [accounts, styles]);

  type Style = typeof stylesWithAccounts[number];

  const handleInspect = useCallback((map: string, accountName = "") => () => {
    if (!accountName) return;

    window.open(`/admin/inspect/${accountName}/maps/${map}/ai`, "_blank");
  }, []);

  const InspectButtonRenderer = ({ data }: ICellRendererParams<Style>) => {
    if (!data) return null;
    const { key, accountName } = data;
    return (
      <Button
        variant="primary"
        onClick={handleInspect(key, accountName)}
        size="xs"
      >
        <FormattedMessage defaultMessage="Inspect" id="button.inspect" />
      </Button>
    );
  };

  const columnDefs: ColDef<Style>[] = useMemo(() => [
    {
      field: "accountName",
      headerName: intl.formatMessage({ defaultMessage: "Account", id: "column.accountName" }),
    },
    {
      field: "key",
      headerName: intl.formatMessage({ defaultMessage: "Key", id: "column.key" }),
    },
    {
      field: "createdAt",
      headerName: intl.formatMessage({ defaultMessage: "Created at", id: "column.createdAt" }),
      sortable: true,
      cellDataType: "dateTimeString",
    },
    {
      field: "updatedAt",
      headerName: intl.formatMessage({ defaultMessage: "Updated at", id: "column.updatedAt" }),
      sortable: true,
      cellDataType: "dateTimeString",
    },
    {
      headerName: intl.formatMessage({ defaultMessage: "Actions", id: "column.actions" }),
      cellRenderer: InspectButtonRenderer,
      sortable: false,
      filter: false,
      resizable: false,
      width: 120,
    },
  ], [intl, InspectButtonRenderer]);

  return (
    <Stack gap={16} h="100%">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Admin", id: "navigation.admin" })}
        current={intl.formatMessage({ defaultMessage: "Chats", id: "navigation.chats" })}
        description={intl.formatMessage({ defaultMessage: "See and manage chats", id: "text.chatsDescription" })}
      />

      <Stack gap="xs">
        <MultiSelect
          onChange={setSelectedAccounts}
          value={selectedAccounts}
          label={intl.formatMessage({ defaultMessage: "Select accounts", id: "label.selectAccounts" })}
          data={accounts.map(({ id, name }) => ({ value: id.toString(), label: name }))}
          limit={20}
          searchable
        />
        <MultiSelect
          onChange={setSelectedSourceStyles}
          value={selectedSourceStyles}
          label={intl.formatMessage({ defaultMessage: "Source map (optional)", id: "label.sourceMap" })}
          data={publicStyles.map(({ id, key }) => ({ value: id.toString(), label: key }))}
          limit={20}
          searchable
        />
      </Stack>
      {isLoading && <Loader />}
      {stylesWithAccounts.length > 0 && (
        <AgGridTable
          rowData={stylesWithAccounts}
          columnDefs={columnDefs}
          domLayout="autoHeight"
          autoSizeStrategy={{
            type: "fitGridWidth",
            defaultMinWidth: 100,
          }}
          suppressHorizontalScroll={false}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
          paginationPageSize={20}
          pagination={true}
        />
      )}
    </Stack>
  );
};
